/** One clickable answer lifted out of an agent reply's numbered list. */
export interface ParsedOption {
  /** The number the agent printed — what the user would have typed. */
  n: number;
  /** Plain-text label (markdown emphasis stripped) — the chip text. */
  label: string;
}

const BOLD = String.raw`(?:\*\*|__)`;
// At most one space of indent: a deeper `1.` is a nested sub-list under an option,
// not an option itself.
//   `1. Text`, `1) Text`, `**1. Text**`
const PLAIN = new RegExp(String.raw`^ ?${BOLD}?(\d{1,2})[.)]\s+(\S.*)$`);
//   `Option 1: Title`, `**Option 1: Title**`, `**Option 1:** Title` — the heading of a
//   block whose description follows on the next lines.
const LABELLED = new RegExp(
  String.raw`^ ?${BOLD}?(?:option|choice)\s+(\d{1,2})${BOLD}?\s*[.):]\s*(?:${BOLD}\s*)?(\S.*)$`,
  'i',
);
// An indented line under a plain option (wrapped text, a `- detail` sub-bullet).
const CONT = /^\s{2,}\S/;
const MAX_OPTIONS = 8;
const MAX_TAIL = 200;

/** Strip the inline markdown an agent wraps a choice in — `**bold**`, `_it_`, `code`. */
const label = (s: string): string =>
  s
    .replace(/(\*\*|__)\s*$/, '') // the lone closer of a `**Option 1: Title**` heading
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/([*_`])(.+?)\1/g, '$2')
    .trim();

interface Start extends ParsedOption {
  line: number;
  /** An `Option N:` heading — its description follows, so its block runs to the next blank line. */
  labelled: boolean;
}

function startOf(text: string, line: number): Start | undefined {
  const l = LABELLED.exec(text);
  if (l) return { line, n: Number(l[1]), label: label(l[2]!), labelled: true };
  const p = PLAIN.exec(text);
  if (p) return { line, n: Number(p[1]), label: label(p[2]!), labelled: false };
  return undefined;
}

/**
 * Pull the numbered options out of an agent reply so the user can click one
 * instead of typing its number. Two shapes are recognised:
 *
 *   1. Category-based tracking          **Option 1: Category-based tracking**
 *   2. Project allocation               Expenses are tied to specific projects…
 *
 * Deliberately conservative — a chip is an answer sent in the user's name, so a
 * miss (falls back to typing) is far cheaper than a false hit. A list counts
 * only when it is the LAST block of the reply, optionally followed by one short
 * closing question ("Which would you like?"), numbered 1..n in order, with
 * 2–{@link MAX_OPTIONS} items. Blank lines between items, indented lines under
 * an item (sub-bullets, wrapped text) and — for `Option N:` headings — the
 * description beneath one are part of the list, not its end. A mid-message plan
 * ("Here's how it goes: 1. … 2. … Then we ship it.") therefore stays plain text.
 *
 * ponytail: line-based heuristic over free-form prose — no agent is instructed
 * to emit a numbered list, so a trailing numbered *plan* still reads as
 * options. Upgrade path is an additive-optional `options` field on the turn
 * wire (the same deferral HomePage records for the confirm-chip timing).
 */
export function parseOptions(text: string): ParsedOption[] {
  const lines = text.split('\n').map((l) => l.trimEnd());
  while (lines.length > 0 && lines[lines.length - 1]!.trim() === '') lines.pop();

  const starts: Start[] = [];
  lines.forEach((l, i) => {
    const s = startOf(l, i);
    if (s) starts.push(s);
  });

  // The LAST run of starts numbered ..k-1, k — an earlier list separated by
  // prose (or a nested sub-list) is not this reply's options. It must reach 1.
  let first = starts.length - 1;
  while (first > 0 && starts[first - 1]!.n === starts[first]!.n - 1) first--;
  const items = starts.slice(Math.max(first, 0));
  if (items.length < 2 || items.length > MAX_OPTIONS || items[0]!.n !== 1) return [];

  // Everything after the last item's own block must be at most one short closing question.
  // A labelled block runs to the next blank line; a plain item owns only the
  // indented lines beneath it (a blank line continues it only if one follows).
  const last = items[items.length - 1]!;
  let end = last.line + 1;
  for (; end < lines.length; end++) {
    const l = lines[end]!;
    if (l.trim() === '') {
      if (last.labelled || !CONT.test(lines[end + 1] ?? '')) break;
    } else if (!last.labelled && !CONT.test(l)) break;
  }
  const closing = lines.slice(end).join(' ').replace(/\s+/g, ' ').trim();
  if (closing !== '' && (closing.length > MAX_TAIL || !closing.endsWith('?'))) return [];

  return items.every((o) => o.label !== '') ? items.map(({ n, label }) => ({ n, label })) : [];
}
